import { Route, Routes } from 'react-router-dom';
import { RoutesConfig } from './routes';
import DashboardLayout from 'components/Layout/DashboardLayout/DashboardLayout';
import ConfigurePage from 'pages/ConfigurePage/ConfigurePage';
import PageNotFound from 'pages/PageNotFound/PageNotFound';
import LoadingScreenOverlayProvider from 'providers/LoadingScreenOverlayProvider/LoadingScreenOverlayProvider';
import LoginPage from 'pages/Auth/LoginPage/LoginPage';
import PrivateRoute from './Routes/PrivateRoute/PrivateRoute';
import PublicRoute from './Routes/PublicRoute/PublicRoute';
import MyModelsPage from 'pages/MyModelsPage/MyModelsPage';
import UsersPage from 'pages/UsersPage/UsersPage';
import ProfilePage from 'pages/ProfilePage/ProfilePage';
import RequestPasswordResetPage from 'pages/Auth/RequestPasswordResetPage/RequestPasswordResetPage';
import ResetPasswordPage from 'pages/Auth/ResetPasswordPage/ResetPasswordPage';
import RegisterPage from 'pages/Auth/RegisterPage/RegisterPage';
import SuccessfulRegistrationPage from 'pages/Auth/SuccessfulRegistrationPage/SuccessfulRegistrationPage';
import { AppMode } from 'store/Common/reducers/common';
import SharedRoute from './Routes/SharedRoute/SharedRoute';
import { DealerPanelModelsPage } from 'pages/DealerPanel/DealerPanelModelsPage/DealerPanelModelsPage';
import { RootRedirect } from './Routes/RootRedirect/RootRedirect';
import CustomModels from 'pages/CustomModels/CustomModels';
import Builder from 'pages/Builder/Builder';
import SingleCustomModel from 'pages/SingleCustomModel/SingleCustomModel';
import { useSelector } from 'react-redux';
import { IRootReducerState } from 'store/store';
import LoadingScreen from 'components/LoadingScreen/LoadingScreen';
import ProtectedRoute from './Routes/ProtectedRoute/ProtectedRoute';
import { PermissionGroupSupplierId } from 'types/Permissions.types';
import BomPage from 'pages/BomPage/BomPage';
import SpecsPage from 'pages/SpecsPage/SpecsPage';
import { useIsVeranoCompany } from 'utils/hooks/useIsVeranoCompany';
import SupplierOrdersPage from 'pages/SupplierOrdersPage/SupplierOrdersPage';
import SupplierSingleOrderPage from 'pages/SupplierSingleOrderPage/SupplierSingleOrderPage';
import SupplierNewOrderPage from 'pages/SupplierNewOrderPage/SupplierNewOrderPage';
import BuyersPage from 'pages/BuyersPage/BuyersPage';
import BuyersSinglePage from 'pages/BuyersSinglePage/BuyersSinglePage';
import MyModelsSinglePage from 'pages/MyModelsSinglePage/MyModelsSinglePage';
import CompanyManagementPage from 'pages/CompanyManagementPage/CompanyManagementPage';
import BuyerOrdersPage from 'pages/BuyerOrdersPage/BuyerOrdersPage';
import BuyerSingleOrderPage from 'pages/BuyerSingleOrderPage/BuyerSingleOrderPage';
import BuyerNewOrderPage from 'pages/BuyerNewOrderPage/BuyerNewOrderPage';
import SuppliersPage from 'pages/SuppliersPage/SuppliersPage';
import CreatePage from 'pages/CompanyLogin/CreatePage/CreatePage';
import SelectPage from 'pages/CompanyLogin/SelectPage/SelectPage';
import DetailsPage from 'pages/CompanyLogin/DetailsPage/DetailsPage';
import CompanyLoginLoginPage from 'pages/CompanyLogin/LoginPage/LoginPage';
// QUOTATIONS DISABLED - see the commented out quotation routes below.
// import BuyerQuotationsPage from 'pages/BuyerQuotationsPage/BuyerQuotationsPage';
// import BuyerNewQuotationPage from 'pages/BuyerNewQuotationPage/BuyerNewQuotationPage';
// import BuyerSingleQuotationPage from 'pages/BuyerSingleQuotationPage/BuyerSingleQuotationPage';
// import BuyerNewOrderFromQuotationPage from 'pages/BuyerNewOrderFromQuotationPage/BuyerNewOrderFromQuotationPage';

export default function AppNavigationRouter() {
  const isLoadingScreenShown = useSelector(
    (state: IRootReducerState) => state.commonInfo.isLoadingScreenShown
  );

  const isExternalHost = useSelector(
    (state: IRootReducerState) => state.commonInfo.isExternalHost
  );

  const isVeranoCompany = useIsVeranoCompany();

  if (isLoadingScreenShown) {
    // Handled in useHandleLoadingScreenState
    // Currently used only for Intent logyx
    return (
      <Routes>
        <Route path="*" element={<LoadingScreen />} />
      </Routes>
    );
  }
  return (
    <Routes>
      <Route path="*" element={<PageNotFound />} />
      <Route element={<DashboardLayout />}>
        <Route path={'/'} element={<PrivateRoute />}>
          <Route path={'/'} element={<RootRedirect />} />
          <Route
            path={'/'}
            element={
              <SharedRoute
                appModes={[AppMode.DEALER_PANEL, AppMode.BUYER]}
                redirectTo={'/'}
              />
            }
          >
            {!isExternalHost && (
              <Route
                path={RoutesConfig.Suppliers.fullPath}
                element={<SuppliersPage />}
              />
            )}
            <Route
              path={RoutesConfig.DealerPanelModels.fullPath}
              element={<DealerPanelModelsPage />}
            />
          </Route>
          <Route
            path={'/'}
            element={
              <SharedRoute
                appModes={[
                  AppMode.BUYER,
                  AppMode.EXTERNAL,
                  AppMode.DEALER_PANEL,
                  AppMode.SUPPLIER,
                ]}
                redirectTo={'/'}
              />
            }
          >
            <Route
              path={RoutesConfig.Configure.fullPath}
              element={<ConfigurePage />}
            />
            <Route path={RoutesConfig.Bom.fullPath} element={<BomPage />} />
            {isVeranoCompany && (
              <Route
                path={RoutesConfig.Specs.fullPath}
                element={<SpecsPage />}
              />
            )}
          </Route>
          {!isExternalHost && (
            <Route
              path={'/'}
              element={
                <SharedRoute
                  appModes={[
                    AppMode.SUPPLIER,
                    AppMode.BUYER,
                    AppMode.DEALER_PANEL,
                  ]}
                  redirectTo={'/'}
                />
              }
            >
              <Route
                path={RoutesConfig.CustomModels.fullPath}
                element={
                  <ProtectedRoute
                    supplierPermissionGroupId={
                      PermissionGroupSupplierId.MODEL_EDITOR
                    }
                  >
                    <CustomModels />
                  </ProtectedRoute>
                }
              />
              <Route
                path={RoutesConfig.SingleCustomModel.fullPath}
                element={
                  <ProtectedRoute
                    supplierPermissionGroupId={
                      PermissionGroupSupplierId.MODEL_EDITOR
                    }
                  >
                    <SingleCustomModel />
                  </ProtectedRoute>
                }
              />
              <Route
                path={RoutesConfig.Builder.fullPath}
                element={
                  <ProtectedRoute
                    supplierPermissionGroupId={
                      PermissionGroupSupplierId.MODEL_EDITOR
                    }
                  >
                    <Builder />
                  </ProtectedRoute>
                }
              />
            </Route>
          )}
          <Route
            path={'/'}
            element={
              <SharedRoute
                appModes={[AppMode.BUYER, AppMode.SUPPLIER]}
                redirectTo={'/'}
              />
            }
          >
            <Route
              path={RoutesConfig.Profile.fullPath}
              element={<ProfilePage />}
            />
            <Route path={RoutesConfig.Users.fullPath} element={<UsersPage />} />
            <Route
              path={RoutesConfig.CompanyManagement.fullPath}
              element={<CompanyManagementPage />}
            />
          </Route>
          <Route
            path={'/'}
            element={
              <SharedRoute appModes={[AppMode.BUYER]} redirectTo={'/'} />
            }
          >
            <Route
              path={RoutesConfig.BuyerOrders.fullPath}
              element={<BuyerOrdersPage />}
            />
            <Route
              path={RoutesConfig.BuyerSingleOrder.fullPath}
              element={<BuyerSingleOrderPage />}
            />
            <Route
              path={RoutesConfig.BuyerNewOrder.fullPath}
              element={<BuyerNewOrderPage />}
            />

            {/* QUOTATIONS DISABLED - the quotation feature is not released to
                staging yet. Routes are commented out so a bookmarked or typed
                URL cannot reach the flow either; with them gone every
                quotation branch in shared code (useIsQuotationMode,
                useQuotationLineResolveParams, useTemplateConfigureParams)
                resolves to the order path. Uncomment together with the nav
                label in Header.tsx, the imports above and the Templates entry
                in ModelConfiguration.tsx.
            <Route
              path={RoutesConfig.BuyerQuotations.fullPath}
              element={<BuyerQuotationsPage />}
            />
            <Route
              path={RoutesConfig.BuyerNewQuotation.fullPath}
              element={<BuyerNewQuotationPage />}
            />
            <Route
              path={RoutesConfig.BuyerSingleQuotation.fullPath}
              element={<BuyerSingleQuotationPage />}
            />
            <Route
              path={RoutesConfig.BuyerNewOrderFromQuotation.fullPath}
              element={<BuyerNewOrderFromQuotationPage />}
            />
            */}
          </Route>
          <Route
            path={'/'}
            element={
              <SharedRoute appModes={[AppMode.SUPPLIER]} redirectTo={'/'} />
            }
          >
            <Route
              path={RoutesConfig.MyModels.fullPath}
              element={<MyModelsPage />}
            />
            <Route
              path={RoutesConfig.MyModelsSingle.fullPath}
              element={<MyModelsSinglePage />}
            />
            <Route
              path={RoutesConfig.Buyers.fullPath}
              element={<BuyersPage />}
            />
            <Route
              path={RoutesConfig.BuyersSingle.fullPath}
              element={<BuyersSinglePage />}
            />
            <Route
              path={RoutesConfig.SupplierOrders.fullPath}
              element={<SupplierOrdersPage />}
            />
            <Route
              path={RoutesConfig.SupplierSingleOrder.fullPath}
              element={<SupplierSingleOrderPage />}
            />
            <Route
              path={RoutesConfig.SupplierNewOrder.fullPath}
              element={<SupplierNewOrderPage />}
            />
          </Route>
        </Route>
      </Route>
      <Route path={'/auth'} element={<PublicRoute />}>
        <Route path={'/auth'} element={<LoadingScreenOverlayProvider />}>
          <Route path={RoutesConfig.Login.fullPath} element={<LoginPage />} />
          <Route
            path={RoutesConfig.Register.fullPath}
            element={<RegisterPage />}
          />
          <Route
            path={RoutesConfig.SuccessfulRegistration.fullPath}
            element={<SuccessfulRegistrationPage />}
          />
          <Route
            path={RoutesConfig.RequestPasswordResetPage.fullPath}
            element={<RequestPasswordResetPage />}
          />
          <Route
            path={RoutesConfig.ResetPassword.fullPath}
            element={<ResetPasswordPage />}
          />
        </Route>
      </Route>
      <Route path={'/company-login'} element={<PublicRoute />}>
        <Route
          path={RoutesConfig.CompanyLogin.fullPath}
          element={<CompanyLoginLoginPage />}
        />
        <Route
          path={RoutesConfig.CompanyLoginCreate.fullPath}
          element={<CreatePage />}
        />
        <Route
          path={RoutesConfig.CompanyLoginSelect.fullPath}
          element={<SelectPage />}
        />
        <Route
          path={RoutesConfig.CompanyLoginDetails.fullPath}
          element={<DetailsPage />}
        />
      </Route>
    </Routes>
  );
}
